import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { List, Span } from '@gaiads/telia-react-component-library';
import { AdminCategory, CategoryTranslation } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { EditableItemInputProps } from '../../../../shared/EditableItem/EditableItemInput/EditableItemInput';
import { RootState } from '../../../../store';
import { selectEditingLocaleId, selectReferenceLocaleId } from '../../../../store/editingLocale';
import { selectLocaleById } from '../../../../store/locales';
import { setNotification } from '../../../../store/ui';
import { getInitialCategoryTranslation } from '../../../../utils/categories';
import { removeEntity, upsertEntity } from '../../../../utils/entities';
import { defaultLocale, getLanguageFromLocaleName } from '../../../../utils/locales';
import { AdminSettingsErrorProps } from '../../AdminSettings';

interface CategoryItemProps extends AdminSettingsErrorProps {
  category: AdminCategory;
  currentCategoryId?: AdminCategory['id'];
  handleClick?: (category: AdminCategory) => void;
  handleCategoryUpdate?: (category: AdminCategory) => Promise<boolean>;
  handleCategoryCreate?: (category: AdminCategory) => Promise<boolean>;
  handleDelete?: (category: AdminCategory) => void;
  handleBlur?: () => void;
  isNew?: boolean;
}

export default function CategoryItem(props: CategoryItemProps) {
  const { category, errors, currentCategoryId, setErrors, handleClick, handleCategoryUpdate, handleCategoryCreate, handleDelete, handleBlur, isNew } =
    props;

  const { t } = useTranslation('adminSettings');

  const dispatch = useDispatch();

  const editingLocaleId = useSelector(selectEditingLocaleId);
  const referenceLocaleId = useSelector(selectReferenceLocaleId);

  const editingLocale = useSelector((state: RootState) => (editingLocaleId ? selectLocaleById(state, editingLocaleId) : undefined));
  const referenceLocale = useSelector((state: RootState) => (referenceLocaleId ? selectLocaleById(state, referenceLocaleId) : undefined));

  const editingTranslation = useMemo(
    () => (editingLocaleId ? category.translations?.find(translation => translation.locale_id === editingLocaleId) : undefined),
    [category, editingLocaleId],
  );
  const referenceTranslation = useMemo(
    () =>
      !isNew && referenceLocaleId
        ? category.translations?.find(translation => translation.locale_id === referenceLocaleId) || getInitialCategoryTranslation(referenceLocaleId)
        : category,
    [category, referenceLocaleId, isNew],
  );

  const editingLanguage = getLanguageFromLocaleName(editingLocale?.name);
  const referenceLanguage = getLanguageFromLocaleName(referenceLocale?.name);
  const fallbackLanguage = defaultLocale.name_in_native_language;

  const handleCategorySave = async (name?: AdminCategory['name']): Promise<boolean> => {
    if (name && name !== '') {
      const updatedCategory: AdminCategory = {
        ...category,
        name,
      };
      if (isNew) {
        return (await handleCategoryCreate?.(updatedCategory)) || false;
      } else {
        return (await handleCategoryUpdate?.(updatedCategory)) || false;
      }
    } else {
      setErrors({
        categories: {
          [category.id]: t('productCategories.errors.nameBlank'),
        },
      });
      if (isNew) {
        dispatch(
          setNotification({
            text: t('productCategories.errors.createCategory'),
            show: true,
            type: 'warning',
          }),
        );
      } else {
        dispatch(
          setNotification({
            text: t('productCategories.errors.updateCategory'),
            show: true,
            type: 'warning',
          }),
        );
      }
      return false;
    }
  };

  const handleTranslationSave = async (translation?: CategoryTranslation, name?: CategoryTranslation['name']): Promise<boolean> => {
    if (translation) {
      let translations;
      if (name && name !== '') {
        const updatedTranslation = {
          ...translation,
          name,
        };
        translations = upsertEntity(category.translations, updatedTranslation);
      } else {
        translations = removeEntity(category.translations, translation);
      }
      const updatedCategory: AdminCategory = {
        ...category,
        translations,
      };
      return (await handleCategoryUpdate?.(updatedCategory)) || false;
    } else return false;
  };

  const handleCategoryBlur = () => {
    setErrors({
      categories: undefined,
    });
    if (handleBlur) handleBlur();
  };

  const onSave: EditableItemInputProps['onSave'] = name => handleTranslationSave(editingTranslation, name);
  const onReferenceSave: EditableItemInputProps['onSave'] =
    !isNew && referenceLocaleId && 'locale_id' in referenceTranslation
      ? name => handleTranslationSave(referenceTranslation, name)
      : handleCategorySave;

  const placeholder = editingLocaleId
    ? t('productCategories.placeholders.categoryTranslation', { language: editingLanguage })
    : t('productCategories.placeholders.category', { language: fallbackLanguage });
  const referencePlaceholder =
    !isNew && referenceLocaleId
      ? t('productCategories.placeholders.categoryTranslation', { language: referenceLanguage })
      : t('productCategories.placeholders.category', { language: fallbackLanguage });

  return (
    <List.Item
      margin={{ size: 'zero' }}
      className={['product-categories__category-item', currentCategoryId === category.id && 'product-categories__category-item--active']}
    >
      <EditableItem
        backgroundHoverColor="gray100"
        divProps={{
          onClick: () => handleClick && handleClick(category),
          clickableAppearance: true,
        }}
      >
        <EditableItem.Input
          showSave
          onSave={onSave}
          value={editingTranslation?.name}
          onBlur={handleCategoryBlur}
          onEditClick={event => event.stopPropagation()}
          onDelete={event => {
            event.stopPropagation();
            if (handleDelete) {
              handleDelete(category);
            }
          }}
          showDelete
          focusOnEdit={!isNew}
          field={content => <Span className="product-categories__category-name">{content}</Span>}
          flexProps={{
            className: 'product-categories__category-field',
          }}
          divProps={{
            className: 'product-categories__category-content',
          }}
          inputProps={{
            placeholder,
            onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLInputElement, MouseEvent>) =>
              event.stopPropagation(),
            'data-testid': `category-${isNew ? 'new' : category.id}-input`,
          }}
          iconFlexProps={{
            className: 'product-categories__category-icons',
          }}
          editIconProps={{
            'data-testid': `category-${isNew ? 'new' : category.id}-edit`,
          }}
          deleteIconProps={{
            'data-testid': `category-${isNew ? 'new' : category.id}-delete`,
          }}
        />
        {referenceTranslation && (
          <EditableItem.Input
            value={referenceTranslation.name}
            edit={isNew}
            isReference
            showSave
            focusOnEdit={true}
            onBlur={handleCategoryBlur}
            onEditClick={event => event.stopPropagation()}
            onSave={onReferenceSave}
            field={content => <Span className="product-categories__category-reference">{content}</Span>}
            flexProps={{
              className: ['product-categories__category-field', 'product-categories__category-reference-field'],
            }}
            divProps={{
              onClick: () => handleClick && handleClick(category),
              clickableAppearance: true,
              className: 'product-categories__category-content',
            }}
            inputProps={{
              required: !!isNew,
              placeholder: referencePlaceholder,
              errorText: errors.categories?.[category.id],
              onClick: (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLInputElement, MouseEvent>) =>
                event.stopPropagation(),
              'data-testid': `category-reference-${isNew ? 'new' : category.id}-input`,
            }}
            iconFlexProps={{
              className: 'product-categories__category-reference-icons',
            }}
            editIconProps={{
              'data-testid': `category-reference-${isNew ? 'new' : category.id}-edit`,
            }}
            deleteIconProps={{
              'data-testid': `category-translation-${isNew ? 'new' : category.id}-delete`,
            }}
          />
        )}
      </EditableItem>
    </List.Item>
  );
}
